/**
 * Pure walkers over the `audition_applications.fields` media blob. No server
 * imports, so a client form can use them; `media-fields.ts` re-exports them for
 * the server readers.
 */

export type MediaFileLike = {
  id: string;
  storagePath: string | null;
  signedUrl?: string;
  [key: string]: unknown;
};

export function isMediaFileLike(value: unknown): value is MediaFileLike {
  return (
    typeof value === 'object' &&
    value !== null &&
    'id' in value &&
    'storagePath' in value
  );
}

/**
 * Deep-collect every distinct MediaFile `id` across all answers, using the same
 * traversal as `collectMediaFilePaths` (media-fields.ts) so a MediaFile wrapped under
 * `entry.media` (the `videos` answer) is reached. These are `artist_media` ids —
 * the join key for `fetchMediaResidency`.
 *
 * Unlike the path collector this does NOT filter on `storagePath` shape: a
 * Meteor-migrated row stores an absolute `https://cdn.ballee.co/…` in
 * `storagePath` (so it is never re-signed) yet still needs its residency, which
 * is exactly the migrated corpus this fixes.
 */
export function collectMediaFileIds(
  fields: Record<string, unknown>,
): Set<string> {
  const ids = new Set<string>();
  const walk = (value: unknown): void => {
    if (Array.isArray(value)) {
      value.forEach(walk);
      return;
    }
    if (value === null || typeof value !== 'object') return;
    if (isMediaFileLike(value) && typeof value.id === 'string' && value.id) {
      ids.add(value.id);
    }
    for (const nested of Object.values(value as Record<string, unknown>)) {
      walk(nested);
    }
  };
  for (const value of Object.values(fields)) walk(value);
  return ids;
}

/**
 * Which media ids the dancer picked or uploaded in this form session: the ids
 * an apply form names as `attachMediaIds` when it saves, so the server attaches
 * them to the application (`sync_own_application_media`).
 *
 * An id already in the form when it loaded is not a pick: an audition admin may
 * have written it into `fields` (#3868 review pass 4). Such an id stays out
 * until the dancer removes it from the form; adding it back after that is a
 * pick. An id the dancer had already attached is not held back: it is theirs.
 *
 * `observe` must see every form change (an RHF `watch` subscription), so that
 * a remove-then-add between two saves still counts as a pick.
 */
export class PickedMediaTracker {
  private readonly heldBack: Set<string>;

  constructor(
    initialValues: Record<string, unknown>,
    attachedMediaIds: Iterable<string> = [],
  ) {
    const attached = new Set([...attachedMediaIds].map(normalizeId));
    this.heldBack = new Set(
      [...idsIn(initialValues)].filter((id) => !attached.has(id)),
    );
  }

  observe(values: Record<string, unknown>): void {
    if (this.heldBack.size === 0) return;
    const present = idsIn(values);
    for (const id of this.heldBack) {
      if (!present.has(id)) this.heldBack.delete(id);
    }
  }

  picked(values: Record<string, unknown>): string[] {
    this.observe(values);
    return [...idsIn(values)].filter((id) => !this.heldBack.has(id));
  }
}

function normalizeId(id: string): string {
  return id.toLowerCase();
}

function idsIn(values: Record<string, unknown>): Set<string> {
  return new Set([...collectMediaFileIds(values)].map(normalizeId));
}
